import {
  AlbumEditForm,
  AlbumShareModal,
  AlbumMoveModal,
  AlbumDeleteModal,
  AlbumCopyModal,
  AlbumDetailsModal,
  FileUpdateContextThumbnailModal,
  SurfaceEditor,
  AlbumCustomCoverEditorPhotos
} from '#components';
import {makeFilesStore} from '~/stores/files.js';
import {markRaw} from 'vue';

export function useAlbumActions() {
  function addEditAlbum({parent, album = null, buttonSaveText = null, showSuccessToast} = {}) {
    //if no album (not editing), and parent cannot have children, display the too many nested album message and reject
    if (parent && !parent.canHaveChildren && !album) {
      useErrorToast().add({
        id: 'add-album-too-deep',
        description: `You've reached the maximum album depth and cannot create an album here.`
      })

      //otherwise open the modal
    } else {
      return new Promise(async resolve => {

        //editing a list item, get detail
        if (album && !album.share_token) {
          album = await useAlbumsStore().getAlbumsOne({id: album.id});
        }

        const modal = useFModal({
          id: 'add-edit-album',
          size: 'lg',
          clickToClose: false,
          component: AlbumEditForm,
          props: {
            album,
            parent,
            buttonSaveText,
            showSuccessToast
          },
          eventHandlers: {
            success({newAlbum}) {
              resolve(newAlbum);
              modal.close();
            }
          }
        });

        modal.open();
      })
    }
  }

  async function share({album}) {

    return new Promise(async resolve => {
      if (!album.share_token) {
        album = await useAlbumsStore().getAlbumsOne({id: album.id});
      }

      const modal = useFModal({
        id: 'album-share-modal',
        size: 'lg',
        component: AlbumShareModal,
        props: {
          album
        },
        eventHandlers: {
          success() {
            resolve();
            modal.close();
          }
        }
      });

      modal.open();
    });
  }

  function move({albums, currentParentId = null} = {}) {
    useFModal({
      id: 'album-move-modal',
      component: AlbumMoveModal,
      props: {
        albums,
        currentParentId
      }
    }).open();
  }

  async function copy({album}) {

    if (album && !album.user) {
      album = await useAlbumsStore().getAlbumsOne({
        id: album.id,
        userId: album.user_id,
        shareToken: album.shareToken
      });
    }

    useFModal({
      id: 'album-copy-modal',
      component: AlbumCopyModal,
      props: {
        album
      }
    }).open();
  }

  async function editCustomCover({album}) {
    if (useDevice().isMobileOrTablet) {
      const mobileModal = useConfirmModal({
        id: 'surface-editor-mobile-no',
        heading: 'Larger Screen Required',
        message: `The Cover Designer isn’t available on smaller screens yet. Please switch to a desktop or laptop to use this tool.`,
        confirmText: 'Ok',
        nonCancelable: true,
        confirmMethod: () => mobileModal.close()
      })
      mobileModal.open();
      return;
    }

    const filesStore = makeFilesStore('custom-album-cover-editor-files-store')();

    //editing from the list, get full album details
    if (album && !album.share_token) {
      album = await useAlbumsStore().getAlbumsOne({id: album.id});
    }

    const {handleExport} = useSurfaceEditorExport({
      exportHandler: async ({exportedSurface}) => {
        const fileBlob = exportedSurface.blob;

        const newFile = await useUploadApi().uploadImage(fileBlob, {
          fileName: `Cover_for_${album.name.replaceAll(' ', '_')}.jpg`,
          takenAt: getFileLastModifiedDate({}), //note: returns correctly formatted today
          uploadSource: useUploadSources().surfaceEditorAlbumCover
        });

        await filesStore.addFilesToContext({ids: [newFile.id], contextId: album.id, contextType: FILE_CONTEXTS.album});
        await useFileActions().setFileAsAlbumCover({album, file: newFile});
        useFilesStore().getFiles({reload: true});
        customAlbumCoverEditorModal.close();
      }
    });

    const customAlbumCoverEditorModal = useFModal({
      id: 'album-cover-editor',
      size: 'fullscreen',
      clickToClose: false,
      showClose: false,
      escToClose: false,
      component: SurfaceEditor,
      props: {
        defaultTheme: SURFACE_EDITOR_COLOR_MODES.light,
        conf: {
          ...ALBUM_COVER_DIMENSIONS,
          projectType: ALBUM_COVER_DESIGN_PROJECT_TYPES,
          designProjectType: ALBUM_COVER_DESIGN_PROJECT_TEMPLATE_TYPE,
          exportActionText: 'Apply Album Cover',
          handleExport
        },
        projectId: album.cover_design_project_id,
        photosTabComponent: markRaw(AlbumCustomCoverEditorPhotos),
        photosTabComponentProps: {album},
        clearFiles: () => {
          filesStore.clearFiles();
        },
        title: `Cover for ${album.name}`
      },
      eventHandlers: {
        projectCreated(project) {
          useAlbumsStore().updateAlbum({id: album.id, updates: {cover_design_project_id: project.id}});
        }
      }
    });
    customAlbumCoverEditorModal.open();
  }

  async function editThumbnail({album}) {
    const {currentUser} = useSessionStore();

    //editing from the list, get full album details
    if (album && !album.share_token) {
      album = await useAlbumsStore().getAlbumsOne({id: album.id});
    }

    const modal = useFModal({
      id: 'edit-album-thumbnail',
      component: FileUpdateContextThumbnailModal,
      size: 'fullscreen',
      showClose: false,
      props: {
        contextType: FILE_CONTEXTS.album,
        context: album,
        canEditCustomCover: album.user?.id === currentUser.id
      },
      eventHandlers: {
        async complete(file) {
          try {
            await useFileActions().setFileAsAlbumCover({album, file});
            modal.close();
          } catch (e) {
            useErrorToast().add();
          }
        },
        editCustomCover() {
          modal.close();
          editCustomCover({album});
        }
      }
    });

    modal.open();
  }

  function deleteAlbums({albums}) {
    return new Promise(resolve => {
      const modal = useFModal({
        id: 'delete-album',
        component: AlbumDeleteModal,
        props: {
          albums
        },
        eventHandlers: {
          success() {
            modal.close();
            resolve();
          }
        }
      });
      modal.open();
    });
  }

  function removeVisitedAlbum({album}) {
    return new Promise(resolve => {
      const albumName = html2text(album.name);
      useConfirmModal({
        heading: `Remove Visited Album?`,
        message: `Remove the album "${albumName}" from your visited albums? This will remove access unless you have the original share link.`,
        confirmMethod: async () => {
          try {
            await useAlbumsStore().removeVisitedAlbums({ids: [album.sharedItemId]});
            useSuccessToast().add({
              title: 'Album Removed',
              description: `The album "${album.name}" has been removed.`
            });
          } catch (e) {
            useErrorToast().add();
          }
        },
        onConfirm: () => resolve()
      }).open();
    });
  }

  function viewDetails({album}) {
    useFModal({
      id: 'album-details',
      component: AlbumDetailsModal,
      size: 'lg',
      props: {
        album
      }
    }).open();
  }

  function getContextActions({album, userId, shareToken}) {
    return {
      upload: {
        label: 'Upload Files',
        icon: COMMON_ICONS.upload,
        click: () => useUploadActions().openUploaderModal({album, userId, shareToken, contextType: FILE_CONTEXTS.album, contextId: album.id})
      },
      addFiles: {
        label: 'Add Files',
        icon: COMMON_ICONS.add,
        click: () => useFileActions().addFilesToContext({context: album, userId, contextType: FILE_CONTEXTS.album, shareToken})
      },
      viewDetails: {
        label: 'View Details',
        icon: 'i-ri-information-2-line',
        click: () => viewDetails({album})
      },
      share: {
        label: 'Share',
        icon: COMMON_ICONS.share,
        click: () => share({album})
      },
      print: {
        label: 'Print',
        icon: COMMON_ICONS.print,
        click: () => {
          if (!album.files_count) {
            useInfoToast().add({
              description: `You must add files to this album before you can Print it.`
            });
          } else {
            useProjectActions().openContainerPrintModal({
              context: album,
              contextType: FILE_CONTEXTS.album
            });
          }
        }
      },
      edit: {
        label: 'Edit Details',
        icon: COMMON_ICONS.edit,
        click: () => addEditAlbum({album})
      },
      editThumbnail: {
        label: 'Change Cover Photo',
        icon: COMMON_ICONS.makeCover,
        click: () => {
          if (!album.files_count) {
            useInfoToast().add({
              description: `You must add files to this album before you can set a cover photo.`
            });
          } else {
            editThumbnail({album});
          }

        }
      },
      editCustomCover: {
        label: 'Cover Designer',
        icon: COMMON_ICONS.surfaceEditor,
        click: () => editCustomCover({album})
      },
      move: {
        label: 'Move Album',
        icon: COMMON_ICONS.moveDrag,
        click: () => move({
          albums: [album],
          currentParentId: album.parent_album?.id
        })
      },
      addNested: {
        label: 'New Nested Album',
        icon: COMMON_ICONS.nestedAlbum,
        click: async () => {
          try {
            await addEditAlbum({parent: album});
          } catch (e) {
            useErrorToast().add('');
          }
        }
      },
      declutter: {
        label: 'Declutter',
        icon: COMMON_ICONS.declutter,
        click: () => {
          useFileActions().declutter({
            context: album,
            contextType: FILE_CONTEXTS.album
          })
        }
      },
      viewSlideshow: {
        label: 'View Slideshow',
        icon: COMMON_ICONS.slideshow,
        click: () => useSlideshow({
          container: album,
          fileContext: FILE_CONTEXTS.album,
          userId,
          shareToken
        }).start()
      },
      download: {
        label: 'Download Album',
        icon: COMMON_ICONS.download,
        click: () => {
          if (!album.files_count) {
            useInfoToast().add({
              description: `You must add files to this album before you can download it.`
            });
          } else {
            useFileActions().downloadContext({
              context: album,
              contextType: FILE_CONTEXTS.album,
              userId
            });
          }
        }
      },
      copy: {
        label: 'Copy Album',
        icon: COMMON_ICONS.copy,
        click: () => copy({album})
      },
      delete: {
        label: 'Delete',
        icon: COMMON_ICONS.delete,
        click: async () => {
          await deleteAlbums({albums: [album]});
          const route = album.parent_album
            ? useBuildRoute().toAlbum({
              album: album.parent_album,
              userId
            })
            : useBuildRoute().toAlbums();
          navigateTo(route);
        }
      },
      removeVisited: {
        label: 'Remove Album',
        icon: 'i-ri-close-circle-line',
        click: () => removeVisitedAlbum({album})
      },
      digitize: {
        label: 'Digitize Media',
        icon: COMMON_ICONS.digitize,
        click: () => useCommonModals().openDigitizationUpsellModal({
          context: DIGITIZATION_UPSELL_CONTEXTS.albumContextMenu
        })
      }
    };
  }

  return {
    addEditAlbum,
    share,
    move,
    copy,
    editThumbnail,
    deleteAlbums,
    removeVisitedAlbum,
    viewDetails,
    getContextActions
  };
}
